import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Home, 
  GraduationCap, 
  BookOpen, 
  Mail, 
  Calendar,
  FileText,
  ArrowRight
} from 'lucide-react';

const Sitemap = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const siteStructure = [
    {
      title: 'Main Pages',
      icon: Home,
      links: [
        { path: '/', label: 'Home', description: 'Welcome to 6th Sense Training Academy' },
        { path: '/about', label: 'About Us', description: 'Learn about our mission and vision' },
        { path: '/services', label: 'Our Services', description: 'Complete study abroad and training solutions' },
        { path: '/contact', label: 'Contact', description: 'Get in touch with us' },
      ]
    },
    {
      title: 'Study Abroad',
      icon: GraduationCap,
      links: [
        { path: '/admissions', label: 'Admissions Guidance', description: 'Comprehensive study abroad services' },
        { path: '/partnered-universities', label: 'Partner Universities', description: '500+ university partnerships' },
        { path: '/visa-success-stories', label: 'Visa Success Stories', description: 'Student visa achievements' },
      ]
    },
    {
      title: 'Training & Courses',
      icon: BookOpen,
      links: [
        { path: '/courses', label: 'All Courses', description: 'IELTS, OET, PTE, DET training' },
        { path: '/ielts-success-stories', label: 'IELTS Success Stories', description: 'IELTS student achievements' },
        { path: '/success-stories', label: 'Success Stories', description: 'Student achievements and testimonials' },
        { path: '/reviews', label: 'Student Reviews', description: 'What our students say' },
      ]
    },
    {
      title: 'Booking & Services',
      icon: Calendar,
      links: [
        { path: '/book-ielts-mock-test', label: 'Book IELTS Mock Test', description: 'Practice with our mock tests' },
        { path: '/book-free-counselling', label: 'Book Free Counselling', description: 'Free study abroad consultation' },
        { path: '/book-free-demo-class', label: 'Book Free Demo Class', description: 'Try our classes for free' },
      ]
    },
    {
      title: 'Resources',
      icon: FileText,
      links: [
        { path: '/gallery', label: 'Gallery', description: 'Photos from events and training' },
        { path: '/privacy', label: 'Privacy Policy', description: 'How we protect your data' },
        { path: '/terms', label: 'Terms of Service', description: 'Terms and conditions' },
        { path: '/sitemap', label: 'Sitemap', description: 'Complete site navigation' },
      ]
    }
  ];

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-slate-900 text-white overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-600/30 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-600/20 mb-6">
              <FileText size={32} className="text-red-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">
              Sitemap
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Find everything you need on our website
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sitemap Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.1 }
              }
            }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {siteStructure.map((section, index) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100">
                    <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                      <Icon size={20} className="text-red-600" />
                    </div>
                    <h2 className="text-xl font-serif font-bold text-slate-900">
                      {section.title}
                    </h2>
                  </div>
                  <ul className="space-y-3">
                    {section.links.map((link, linkIndex) => (
                      <li key={linkIndex}>
                        <Link
                          to={link.path}
                          className="group flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                        >
                          <ArrowRight 
                            size={16} 
                            className="text-slate-400 group-hover:text-red-600 mt-1 flex-shrink-0 transition-colors" 
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-900 group-hover:text-red-600 transition-colors">
                              {link.label}
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {link.description}
                            </div>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Quick Links Section */}
          <motion.div
            variants={fadeInUp}
            className="mt-12 bg-gradient-to-br from-red-50 to-red-100 rounded-2xl p-8 border border-red-200"
          >
            <h2 className="text-2xl font-serif font-bold text-slate-900 mb-6 text-center">
              Quick Links
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link
                to="/book-free-counselling"
                className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow group"
              >
                <Mail className="mx-auto mb-2 text-red-600 group-hover:scale-110 transition-transform" size={24} />
                <div className="font-semibold text-slate-900 group-hover:text-red-600 transition-colors">
                  Free Counselling
                </div>
              </Link>
              <Link
                to="/book-ielts-mock-test"
                className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow group"
              >
                <Calendar className="mx-auto mb-2 text-red-600 group-hover:scale-110 transition-transform" size={24} />
                <div className="font-semibold text-slate-900 group-hover:text-red-600 transition-colors">
                  Mock Test
                </div>
              </Link>
              <Link
                to="/courses"
                className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow group"
              >
                <BookOpen className="mx-auto mb-2 text-red-600 group-hover:scale-110 transition-transform" size={24} />
                <div className="font-semibold text-slate-900 group-hover:text-red-600 transition-colors">
                  All Courses
                </div>
              </Link>
              <Link
                to="/contact"
                className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-shadow group"
              >
                <Mail className="mx-auto mb-2 text-red-600 group-hover:scale-110 transition-transform" size={24} />
                <div className="font-semibold text-slate-900 group-hover:text-red-600 transition-colors">
                  Contact Us
                </div>
              </Link>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            variants={fadeInUp}
            className="mt-8 bg-white rounded-2xl p-8 shadow-sm border border-slate-200"
          >
            <h2 className="text-2xl font-serif font-bold text-slate-900 mb-6 text-center">
              Contact Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-sm text-slate-500 mb-2">Phone</div>
                <div className="font-semibold text-slate-900">+91 63837 03636</div>
                <div className="text-sm text-slate-600">+91 63837 11250</div>
                <div className="text-sm text-slate-600">+91 73059 59496</div>
              </div>
              <div>
                <div className="text-sm text-slate-500 mb-2">Email</div>
                <div className="font-semibold text-slate-900">manager.6thsense@gmail.com</div>
                <div className="text-sm text-slate-600">meena.elptraining@gmail.com</div>
              </div>
              <div>
                <div className="text-sm text-slate-500 mb-2">Address</div>
                <div className="text-sm text-slate-600">2nd Floor, Vignesh Plaza, 202, Thillai Nagar Main Rd, above Domino's Pizza, Tennur, Tiruchirappalli, Tamil Nadu 620018</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Sitemap;

